import { useState } from 'react';
import { api, ApiError, setTabletCredential } from '../lib/api';
import {
  assertPinShape, cacheLogins, preparePin, prepareTablet, weakPinReason,
} from '../lib/credential';
import { unlockWithPin } from '../lib/session';

/**
 * Registering this tablet — done once per physical tablet.
 *
 * Not in the prototype (a single-user demo); built from BRIEF.md,
 * "Authentication", and the 23 September 2026 decisions: a tablet is
 * registered on its own, and the owner sets people's PINs when he creates
 * their logins, so anyone with a login can use any registered tablet.
 *
 * Two ways in:
 *
 *   tablet code    the owner makes one in Settings → Tablets and reads it out.
 *                  The tablet mints its credential T, registers SHA-256(T),
 *                  fetches who can log in, and that is that.
 *   recovery code  the last owner's way back, and how the very first tablet is
 *                  issued (`npm run bootstrap`). One code does both jobs:
 *                  registers the tablet and sets the owner's PIN, which then
 *                  works on every registered tablet.
 *
 * The PIN is never sent anywhere; the server gets a credential wrapped under
 * a key derived from it.
 */

type Mode = 'tablet' | 'recovery';
type Step = 'code' | 'pin';

interface Props {
  onDone: () => void;
}

export default function RegisterTablet({ onDone }: Props) {
  const [mode, setMode] = useState<Mode>('tablet');
  const [step, setStep] = useState<Step>('code');
  const [code, setCode] = useState('');
  const [label, setLabel] = useState('');
  const [pin, setPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  async function registerWithTabletCode() {
    setBusy(true);
    setError('');
    try {
      const tablet = await prepareTablet();
      const result = await api.registerTablet(code.trim(), tablet.credentialHash, label.trim() || 'Tablet');
      await tablet.seal({ tabletId: result.tabletId, label: result.label });
      setTabletCredential(tablet.credential);
      // Who can log in, cached so the login screen works from here on, offline too.
      const logins = await api.logins();
      await cacheLogins(logins.logins);
      onDone();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not reach the office. Try again.');
    } finally {
      setBusy(false);
    }
  }

  async function recover() {
    setError('');
    try {
      assertPinShape(pin);
    } catch {
      setError('A PIN is exactly 4 digits.');
      return;
    }
    if (pin !== confirmPin) {
      setError('The two PINs are not the same.');
      return;
    }
    const weak = weakPinReason(pin);
    if (weak) {
      setError(weak);
      return;
    }

    setBusy(true);
    try {
      // Mint both, register second, store third — a failed call leaves nothing
      // on the tablet that the office does not know about.
      const tablet = await prepareTablet();
      const owner = await preparePin(pin);
      const result = await api.recover(code.trim(), label.trim() || 'Tablet', tablet.credentialHash, owner.pin);
      await tablet.seal({ tabletId: result.tabletId, label: result.label });
      setTabletCredential(tablet.credential);
      const logins = await api.logins();
      await cacheLogins(logins.logins);
      // Straight in — no reason to make him type the PIN he just chose.
      await unlockWithPin(result.user.id, pin);
      onDone();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not set up this tablet. Try again.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="login">
      <div className="loginbox" style={{ maxWidth: 420 }}>
        <div style={{ fontSize: 21, fontWeight: 500 }}>Register this tablet</div>

        {step === 'code' ? (
          <>
            <div className="sub" style={{ marginTop: 4, marginBottom: 16 }}>
              {mode === 'tablet'
                ? 'Ask the owner for a tablet code. Once this tablet is registered, anyone with a login can use it with their PIN.'
                : 'Type the recovery code the owner wrote down when the app was set up.'}
            </div>

            <div style={{ textAlign: 'left' }}>
              <div className="sub" style={{ marginBottom: 4 }}>
                {mode === 'tablet' ? 'Tablet code' : 'Recovery code'}
              </div>
              <input
                type="text"
                inputMode="text"
                autoCapitalize="characters"
                autoCorrect="off"
                spellCheck={false}
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder={mode === 'tablet' ? 'K7M2-QX9F-3TBR' : 'K7M2-QX9F-3TBR-…'}
                style={{ fontFamily: 'ui-monospace, monospace', letterSpacing: '0.08em' }}
              />

              <div className="sub" style={{ margin: '14px 0 4px' }}>
                What to call this tablet
              </div>
              <input
                type="text"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                placeholder="Office tablet"
              />
            </div>

            <div className="err" role="alert">
              {error}
            </div>

            <div className="bar" style={{ marginTop: 6 }}>
              <button
                type="button"
                className="btn pri"
                disabled={busy || code.trim().length < 8}
                onClick={() => {
                  // A recovery code is five groups of four; a tablet code is
                  // three. Typed into the wrong box, it was refused with no
                  // hint (25 September 2026) — the switch is a small link
                  // below the button. Take it as what it plainly is.
                  const looksLikeRecovery = code.replace(/[^0-9a-z]/gi, '').length >= 20;
                  if (mode === 'tablet' && looksLikeRecovery) {
                    setMode('recovery');
                    setError('');
                    setStep('pin');
                  } else if (mode === 'tablet') {
                    void registerWithTabletCode();
                  } else {
                    setStep('pin');
                  }
                }}
              >
                {busy ? 'Registering…' : mode === 'tablet' ? 'Register this tablet' : 'Continue'}
              </button>
            </div>

            <button
              type="button"
              className="btn sm"
              style={{ marginTop: 18, minWidth: 0 }}
              disabled={busy}
              onClick={() => {
                setMode(mode === 'tablet' ? 'recovery' : 'tablet');
                setCode('');
                setError('');
              }}
            >
              {mode === 'tablet' ? 'I have a recovery code' : 'I have a tablet code'}
            </button>
          </>
        ) : (
          <>
            <div className="sub" style={{ marginTop: 4, marginBottom: 16 }}>
              Choose your 4-digit PIN. It will work for you on every registered tablet. The
              PIN itself is never sent to the office.
            </div>

            <div style={{ textAlign: 'left' }}>
              <div className="sub" style={{ marginBottom: 4 }}>
                New PIN
              </div>
              <input
                type="password"
                inputMode="numeric"
                autoComplete="new-password"
                maxLength={4}
                value={pin}
                onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))}
                style={{ letterSpacing: '0.5em', fontSize: 20, textAlign: 'center' }}
              />

              <div className="sub" style={{ margin: '14px 0 4px' }}>
                Type it again
              </div>
              <input
                type="password"
                inputMode="numeric"
                autoComplete="new-password"
                maxLength={4}
                value={confirmPin}
                onChange={(e) => setConfirmPin(e.target.value.replace(/\D/g, ''))}
                style={{ letterSpacing: '0.5em', fontSize: 20, textAlign: 'center' }}
              />
            </div>

            <div className="banner b-warn" style={{ marginTop: 14, fontSize: 13 }}>
              Five wrong PINs lock you out of a tablet until another owner sets you a new PIN.
            </div>

            <div className="err" role="alert">
              {error}
            </div>

            <div className="bar">
              <button type="button" className="btn" onClick={() => setStep('code')} disabled={busy}>
                Back
              </button>
              <button
                type="button"
                className="btn pri"
                onClick={() => void recover()}
                disabled={busy || pin.length !== 4 || confirmPin.length !== 4}
              >
                {busy ? 'Setting up…' : 'Set up this tablet'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
